import * as React from 'react';

import { useSelector } from 'react-redux';
import { ActionLink, ConfirmationInput, UIText1 } from '@cian/ui-kit';
import {
  trackAntifraudPopupChangePhone,
  trackAntifraudPopupConfirm,
  trackAntifraudPopupResendCode,
  trackAntifraudPopupShow,
} from './tracking/trackAntifraudPopup';

import { PhoneConfirmation } from '../../components/PhoneConfirmation';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { selectPhoneConfirmationErrorText } from '../../selectors/phoneConfirmation/selectPhoneConfirmationErrorText';
import { selectPhoneConfirmationStatus } from '../../selectors/phoneConfirmation/selectPhoneConfirmationStatus';
import { selectSecondsToRetry } from '../../selectors/phoneConfirmation/selectSecondsToRetry';

import { setPhoneConfirmationError, openRequestForm } from '../../slices';
import { requestPhoneValidationCode } from '../../thunks/phoneValidationCode';
import { TConfirmationStatus } from '../../types/phoneConfirmation';
import { CountdownTimer } from '../CountdownTimer/CountdownTimer';
import { preparePhoneDisplay } from '../RequestFormModalWindow/utils/phone';

export const PhoneConfirmationContainer = ({
  phone,
  isSubmitting,
  handleSubmit,
}: {
  phone: string;
  isSubmitting: boolean;
  handleSubmit(code: string): void;
}) => {
  const dispatch = useAppDispatch();
  const [value, setValue] = React.useState('');

  const errorText = useSelector(selectPhoneConfirmationErrorText);
  const formatedPhone = preparePhoneDisplay(phone);
  const [lastAttemptTimestamp, setLastAttemptTimestamp] = React.useState<number | null>(new Date().getTime());
  const [resendAvailable, setResendAvailable] = React.useState(false);
  const [isTouched, setIsTouched] = React.useState(false);
  const secondsToRetry = useSelector(selectSecondsToRetry);
  const sendSmsStatus: TConfirmationStatus = useSelector(selectPhoneConfirmationStatus);
  const isLoading = sendSmsStatus === 'pending' || isSubmitting;

  const handleComplete = (val: string) => {
    trackAntifraudPopupConfirm();
    handleSubmit(val);
    setIsTouched(false);
  };

  const handleChangeNumber = () => {
    trackAntifraudPopupChangePhone();
    dispatch(setPhoneConfirmationError({ error: undefined }));
    dispatch(openRequestForm());
  };

  const handleChangeValue = (val: string) => {
    setValue(val);
    if (!isTouched && errorText) {
      dispatch(setPhoneConfirmationError({ error: undefined }));
    }
    setIsTouched(true);
  };

  const handleTimeoutExpired = React.useCallback(() => {
    setResendAvailable(true);
  }, []);

  const handleResendClick = React.useCallback(() => {
    trackAntifraudPopupResendCode();
    dispatch(requestPhoneValidationCode({ phone: `+${phone}` }));
  }, [dispatch, phone]);

  React.useEffect(() => {
    if (sendSmsStatus === 'rejected') {
      setLastAttemptTimestamp(null);
    } else if (sendSmsStatus === 'fulfilled') {
      setLastAttemptTimestamp(new Date().getTime());
    } else if (sendSmsStatus === 'pending') {
      setLastAttemptTimestamp(null);
      setResendAvailable(false);
    }
  }, [sendSmsStatus, secondsToRetry]);

  React.useEffect(() => {
    trackAntifraudPopupShow();
  }, []);

  return (
    <PhoneConfirmation
      status={sendSmsStatus}
      error={errorText}
      phone={formatedPhone}
      changeNumber={<ActionLink onClick={handleChangeNumber}>Изменить номер</ActionLink>}
      confirmationInput={
        <ConfirmationInput
          autoFocus
          disabled={isLoading}
          invalid={!!errorText}
          format="9999"
          value={value}
          onChange={handleChangeValue}
          onComplete={handleComplete}
        />
      }
      countdown={
        !resendAvailable && !isLoading && lastAttemptTimestamp ? (
          <UIText1 display="inline" color="icon-secondary-default">
            Отправить код повторно через{' '}
            {
              <CountdownTimer
                fromTimestamp={lastAttemptTimestamp}
                timeout={secondsToRetry}
                onTimeoutExpired={handleTimeoutExpired}
              />
            }{' '}
            сек
          </UIText1>
        ) : null
      }
      resend={
        isLoading ? (
          <UIText1 color="text-primary-disabled">Отправить код повторно</UIText1>
        ) : (
          <ActionLink onClick={handleResendClick}>Отправить код повторно</ActionLink>
        )
      }
    />
  );
};
